import { Link } from 'react-router-dom';
import {
  resolveEntitlementState, subscriptionRestrictionCopy, type FeatureSummary, type PlanAccess,
} from '../features/subscription/types';

/**
 * The shared plan/subscription Access Restriction notice (approved Access Restriction UI States SRS §16/§18/§19):
 * names the restriction's real cause, and — for a plan restriction — the current plan, the required plan, and
 * the feature being unlocked. It is shown for plan/subscription causes ONLY: a missing role permission is a
 * different restriction ("Permission Required") and must never be dressed up as an upgrade prompt, so this
 * renders nothing when the feature is enabled or not yet usable (Coming soon, Pilot only and Not available right now are not restrictions).
 *
 * Display only — the backend filter on the protected endpoint is the real boundary.
 */
export function AccessRestrictionNotice({ feature, access }: { feature: FeatureSummary; access: PlanAccess }) {
  const state = resolveEntitlementState(feature, access);

  // Coming soon, Pilot only and Not available right now are not plan restrictions: no plan unlocks them, so no upgrade is ever offered.
  if (state === 'enabled' || state === 'coming-soon' || state === 'pilot-only' || state === 'unavailable') return null;

  if (state === 'subscription-required') {
    const copy = subscriptionRestrictionCopy(access.effectiveState);
    return (
      <section className="access-restriction" role="region" aria-label="Subscription restriction" data-testid="access-restriction-subscription">
        <h3 className="access-restriction__title"><span aria-hidden="true">🔒</span> {copy.title}</h3>
        <p>{copy.message}</p>
        <dl className="access-restriction__facts">
          <div><dt>Feature</dt><dd>{feature.displayName}</dd></div>
          <div><dt>Selected plan</dt><dd>{access.planName ?? '—'}</dd></div>
        </dl>
        <Link className="access-restriction__cta" to="/settings/subscription">View Plans</Link>
      </section>
    );
  }

  return (
    <section className="access-restriction" role="region" aria-label="Upgrade required" data-testid="access-restriction-upgrade">
      <h3 className="access-restriction__title"><span aria-hidden="true">🔒</span> Upgrade Required</h3>
      <p>This feature is not included in your current plan.</p>
      <dl className="access-restriction__facts">
        <div><dt>Feature</dt><dd>{feature.displayName}</dd></div>
        <div><dt>Current plan</dt><dd>{access.planName ?? 'No plan selected'}</dd></div>
        <div><dt>Required plan</dt><dd>{feature.minimumPlanName ?? 'A higher plan'}</dd></div>
      </dl>
      <Link className="access-restriction__cta" to="/settings/subscription">Upgrade Plan</Link>
    </section>
  );
}
